<script setup lang="ts">
import GoalShort from '@/vue/molecules/goal-short.vue';
import {
  type Goal,
  SessionState,
} from '@/ts/types/dto/interventions-dto';
import LazyImage from '@/vue/atoms/lazy-image.vue';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

defineProps<{
  description?: string;
  title?: string;
  goalsTitle: string;
  image?: UuidDTO;
  goals: Goal[];
  finished?: boolean;
  state?: SessionState;
  hideDescription?: boolean;
}>();
</script>
<template>
  <lazy-image v-if="image" :src="image" :width="768" class="image"/>
  <div v-if="title && state !== SessionState.COMPLETED" class="summary">
    <h2>{{ title }}</h2>
    <div v-safe-html="description"/>
    <slot/>
  </div>

  <div v-if="goals.length" class="goals-section">
    <h2>{{ goalsTitle }}</h2>
    <div class="goals" tabindex="0">
      <goal-short
        v-for="(goal, i) in goals"
        :key="goal.id"
        :order="i + 1"
        :goal="goal"
        :finished="finished"
        :hide-description="hideDescription"
      />
    </div>
  </div>
</template>
<style scoped lang="scss">
.image {
  height: 400px;
  flex-shrink: 0;
  border-radius: var(--card-border-radius);
}

h2 {
  @include font-h3-bold;
  margin-bottom: var(--Spacing-spacing-4);
}

.summary {
  @include q-card-0;
  gap: var(--Spacing-spacing-6);
  padding: var(--Spacing-spacing-4);
  @include screen-lg {
    padding: var(--Spacing-spacing-7);
  }
}

.goals {
  display: flex;
  flex-direction: row;
  gap: var(--Spacing-spacing-4);
  container-type: inline-size;

  // Mobile: horizontal slider with snap
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding-bottom: var(--Spacing-spacing-2);

  // Hide scrollbar but keep functionality
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  :deep(.goal) {
    scroll-snap-align: start;
    flex-shrink: 0;
  }

  // Desktop: wrap normally
  @include screen-md {
    flex-wrap: wrap;
    overflow-x: visible;
    scroll-snap-type: none;
    padding-bottom: 0;

    :deep(.goal) {
      flex-shrink: 1;
    }
  }
}
</style>
